import { api, handleApiError } from '../client'
import { buildODataQuery, type ODataQueryParams } from '../odata'
import type {
  CreateUserRequest,
  UpdateUserRequest,
  Permission,
} from '../types'

export interface UserOData {
  id: string
  email: string
  firstName: string
  lastName: string
  fullName: string
  isActive: boolean
  isEmailVerified: boolean
  isSuperAdmin: boolean
  lastLoginAt?: string
  createdAt: string
  updatedAt?: string
  roles?: UserRoleOData[]
}

export interface UserRoleOData {
  id: string
  name: string
  description?: string
  isSystemRole: boolean
  isDefault: boolean
  createdAt: string
  updatedAt: string
  permissions: Permission[]
}

export interface UsersODataResponse {
  value: UserOData[]
  '@odata.count'?: number
}

/** User search result item */
export interface UserSearchItem {
  id: string
  email: string
  fullName: string
  avatarUrl?: string
}

/** User search result response */
export interface UserSearchResult {
  users: UserSearchItem[]
  isExternalEmail: boolean
  externalEmail: string | null
}

/**
 * Normalizes an object's keys from PascalCase to camelCase.
 * OData responses use PascalCase, but frontend expects camelCase.
 */
function normalizeKeys<T>(obj: Record<string, unknown>): T {
  const result: Record<string, unknown> = {}
  for (const key in obj) {
    const camelKey = key.charAt(0).toLowerCase() + key.slice(1)
    const value = obj[key]
    // Recursively normalize nested objects and arrays
    if (Array.isArray(value)) {
      result[camelKey] = value.map(item =>
        typeof item === 'object' && item !== null
          ? normalizeKeys(item as Record<string, unknown>)
          : item
      )
    } else if (typeof value === 'object' && value !== null) {
      result[camelKey] = normalizeKeys(value as Record<string, unknown>)
    } else {
      result[camelKey] = value
    }
  }
  return result as T
}

export const userService = {
  /**
   * Searches for users by email or name.
   * Returns matching users and detects if query is an external email.
   */
  async searchUsers(query: string, limit: number = 10): Promise<UserSearchResult> {
    try {
      const response = await api.get<UserSearchResult>('/v1/users/search', {
        params: { query, limit }
      })
      return normalizeKeys<UserSearchResult>(response.data as unknown as Record<string, unknown>)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets users with OData query support.
   * Supports filtering, sorting, paging via OData parameters.
   */
  async getUsers(params?: ODataQueryParams): Promise<UsersODataResponse> {
    try {
      const queryString = params ? buildODataQuery(params) : ''
      const response = await api.get<UsersODataResponse | UserOData[]>(`/v1/users${queryString}`)

      // Handle both OData response and array response for backwards compatibility
      if (Array.isArray(response.data)) {
        return {
          value: response.data.map(item => normalizeKeys<UserOData>(item as unknown as Record<string, unknown>)),
          '@odata.count': response.data.length
        }
      }

      // Normalize OData response - keys are PascalCase from OData
      const data = response.data as { value?: unknown[]; '@odata.count'?: number }
      return {
        value: (data.value || []).map(item => normalizeKeys<UserOData>(item as unknown as Record<string, unknown>)),
        '@odata.count': data['@odata.count']
      }
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getUser(id: string): Promise<UserOData> {
    try {
      const response = await api.get<UserOData>(`/v1/users/${id}`)
      return normalizeKeys<UserOData>(response.data as unknown as Record<string, unknown>)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getCurrentUser(): Promise<UserOData> {
    try {
      const response = await api.get<UserOData>('/v1/users/me')
      return normalizeKeys<UserOData>(response.data as unknown as Record<string, unknown>)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async createUser(data: CreateUserRequest): Promise<UserOData> {
    try {
      const response = await api.post<UserOData>('/v1/users', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async updateUser(id: string, data: UpdateUserRequest): Promise<UserOData> {
    try {
      const response = await api.put<UserOData>(`/v1/users/${id}`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async updateCurrentUser(data: UpdateUserRequest): Promise<UserOData> {
    try {
      const response = await api.put<UserOData>('/v1/users/me', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async deactivateUser(id: string): Promise<void> {
    try {
      await api.post(`/v1/users/${id}/deactivate`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async activateUser(id: string): Promise<void> {
    try {
      await api.post(`/v1/users/${id}/activate`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async assignRoles(userId: string, roleIds: string[]): Promise<void> {
    try {
      await api.post(`/v1/users/${userId}/roles`, { roleIds })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async removeRole(userId: string, roleId: string): Promise<void> {
    try {
      await api.delete(`/v1/users/${userId}/roles/${roleId}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async inviteUser(email: string, roleIds?: string[]): Promise<void> {
    try {
      await api.post('/v1/users/invite', { email, roleIds })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async resendInvite(userId: string): Promise<void> {
    try {
      await api.post(`/v1/users/${userId}/resend-invite`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async uploadAvatar(userId: string, file: File): Promise<string> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post<{ avatarUrl: string }>(
        `/v1/users/${userId}/avatar`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      return response.data.avatarUrl
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async deleteAvatar(userId: string): Promise<void> {
    try {
      await api.delete(`/v1/users/${userId}/avatar`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async exportUsers(format: 'csv' | 'xlsx' = 'csv'): Promise<Blob> {
    try {
      const response = await api.get<Blob>(`/v1/users/export`, {
        params: { format },
        responseType: 'blob',
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
